import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Search } from 'lucide-react';
import { alerts, districts } from '../mock';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Escalations = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.message.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (alert.site && alert.site.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || alert.type === filterType;
    return matchesSearch && matchesType;
  });

  const getAlertStyle = (type) => {
    if (type === 'critical') return { bg: '#fee', color: '#e74c3c', border: '#e74c3c' };
    if (type === 'warning') return { bg: '#fff4e6', color: '#f97316', border: '#f39c12' };
    return { bg: '#e8f5f1', color: '#10ac84', border: '#10ac84' };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Escalations & Alerts</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Real-time system alerts and escalation management</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#e74c3c' }}>{alerts.filter(a => a.type === 'critical').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Critical Alerts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#f97316' }}>{alerts.filter(a => a.type === 'warning').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Warnings</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{alerts.filter(a => a.type === 'info').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Info</div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Alert Type Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Critical', 'Warning', 'Info'],
                  datasets: [{
                    data: [
                      alerts.filter(a => a.type === 'critical').length,
                      alerts.filter(a => a.type === 'warning').length,
                      alerts.filter(a => a.type === 'info').length
                    ],
                    backgroundColor: [
                      'rgba(231, 76, 60, 0.8)',
                      'rgba(243, 156, 18, 0.8)',
                      'rgba(16, 172, 132, 0.8)'
                    ],
                    borderColor: [
                      '#e74c3c',
                      '#f39c12',
                      '#10ac84'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Alerts by District</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: districts.map(d => d.name),
                  datasets: [{
                    label: 'Alerts',
                    data: districts.map(d => alerts.filter(a => a.district === d.id).length),
                    backgroundColor: 'rgba(52, 152, 219, 0.8)',
                    borderColor: '#3498db',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                        color: '#576574',
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#576574' }} />
              <Input
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => {
          const alertStyle = getAlertStyle(alert.type);
          const district = alert.district ? districts.find(d => d.id === alert.district) : null;
          
          return (
            <Card key={alert.id} onClick={() => alert.site && navigate(`/site/${alert.site}`)} className={`hover:shadow-lg transition-shadow ${alert.site ? 'cursor-pointer' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: alertStyle.bg }}>
                    <AlertTriangle className="w-6 h-6" style={{ color: alertStyle.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge variant="outline" className="mb-2" style={alertStyle}>
                          {alert.type.toUpperCase()}
                        </Badge>
                        {alert.site && (
                          <Badge variant="outline" className="ml-2">{alert.site}</Badge>
                        )}
                        {district && (
                          <Badge variant="outline" className="ml-2">{district.name}</Badge>
                        )}
                      </div>
                      <div className="text-sm" style={{ color: '#576574' }}>{alert.timestamp}</div>
                    </div>
                    <p className="text-lg font-semibold mb-2" style={{ color: '#0a3d62' }}>{alert.message}</p>
                    {alert.type === 'critical' && (
                      <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#fff4e6' }}>
                        <div className="text-sm font-semibold mb-1" style={{ color: '#f97316' }}>Recommended Action:</div>
                        <div className="text-sm" style={{ color: '#576574' }}>Immediate technical inspection required. Dispatch maintenance team to site.</div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Escalations;
